import { Route, RouteProps } from "react-router-dom";

import { BaseAppRoute, LDFlagNames } from "Utilities/types";

import { OrganisationLevelSection } from "@eolas-medical/core";
import { useLaunchDarkly } from "Contexts";
import { useEolasNavigation } from "Components/Navigation/hooks";
import OrganisationAdmin from "./OrganisationAdmin";
import { OrganisationAdminRoute } from "Pages/Spaces/types";
import { ContentManagement } from "./pages/ContentManagement";
import { DrugMonitoring } from "./pages/DrugMonitoring";
import { ActivateMedusaPage } from "./pages/ContentManagement/pages/MedusaActivationPage";
import {
  LearningManagement,
  LearningManagementSection,
} from "./pages/MiniApp/LearningManagementSystem";
import { CommunicationsPortal } from "./pages/MiniApp/CommunicationsPortal";
import { OrganisationWellbeing, OrganisationWellbeingSection } from "./pages/OrganisationWellbeing";
import { GenericGuidelines, GenericGuidelinesSection } from "./pages/GenericGuidelines";
import { Guidelines, GuidelinesSection } from "../Space/pages/Guidelines";
import { AdminContactsPage, AdminContactsSection } from "../Space/pages/MiniApp/Contacts";
import ManageUsers from "./pages/ManageUsers/ManageUsers";
import { ExpiredFilesPage } from "Pages/Spaces/components/ExpiredFiles";
import ContentRepository from "./pages/ContentRepository/ContentRepository";

export const adminOrganisationRoutes: Record<OrganisationAdminRoute, string> = {
  contentManagement: `/${BaseAppRoute.organisation}/:organisationName/content-management`,
  drugMonitoring: `/${BaseAppRoute.organisation}/:organisationName/content-management/${OrganisationLevelSection.drugMonitoring}`,
  hospitalContacts: `/${BaseAppRoute.organisation}/:organisationName/content-management/${OrganisationLevelSection.hospitalContacts}`,
  hospitalContactsSubsection: `/${BaseAppRoute.organisation}/:organisationName/content-management/${OrganisationLevelSection.hospitalContacts}/:sectionID`,
  hospitalGuidelines: `/${BaseAppRoute.organisation}/:organisationName/content-management/${OrganisationLevelSection.hospitalGuidelines}`,
  hospitalGuidelinesSubsection: `/${BaseAppRoute.organisation}/:organisationName/content-management/${OrganisationLevelSection.hospitalGuidelines}/:sectionID`,
  expiredGuidelines: `/${BaseAppRoute.organisation}/:organisationName/content-management/expired-guidelines`,
  medusaMedications: `/${BaseAppRoute.organisation}/:organisationName/content-management/medusaMedications`,
  learningManagement: `/${BaseAppRoute.organisation}/:organisationName/content-management/${OrganisationLevelSection.learningManagement}`,
  learningManagementSubsection: `/${BaseAppRoute.organisation}/:organisationName/content-management/${OrganisationLevelSection.learningManagement}/:sectionID`,
  communicationPortal: `/${BaseAppRoute.organisation}/:organisationName/content-management/${OrganisationLevelSection.communicationPortal}`,
  organisationWellbeing: `/${BaseAppRoute.organisation}/:organisationName/content-management/${OrganisationLevelSection.organisationWellbeing}`,
  organisationWellbeingSection: `/${BaseAppRoute.organisation}/:organisationName/content-management/${OrganisationLevelSection.organisationWellbeing}/:sectionID`,
  genericOrganisationGuidelines: `/${BaseAppRoute.organisation}/:organisationName/content-management/${OrganisationLevelSection.genericOrganisationGuidelines}`,
  genericOrganisationGuidelinesSection: `/${BaseAppRoute.organisation}/:organisationName/content-management/${OrganisationLevelSection.genericOrganisationGuidelines}/:sectionID`,
  genericOrganisationGuidelinesExpired: `/${BaseAppRoute.organisation}/:organisationName/content-management/generic-expired-guidelines`,
  contentRepository: `/${BaseAppRoute.organisation}/:organisationName/content-management/content-repository/:contentRepositoryId`,
};

export const OrganisationAdminRoutes = () => {
  const { flags } = useLaunchDarkly();
  const { hasOrganisationTab, hasSpacesTab } = useEolasNavigation();
  const showContentRepository = !!flags[LDFlagNames.GENERIC_CONTENT_REPOSITORY];

  // HOTFIX: 09/06/2023
  const hideManageUsers = hasOrganisationTab && hasSpacesTab;

  return (
    <>
      <Route
        exact
        path={`/${BaseAppRoute.organisation}/:organisationName`}
        component={OrganisationAdmin}
      />

      {showContentRepository && (
        <Route path={adminOrganisationRoutes.contentRepository} component={ContentRepository} />
      )}

      <Route exact path={adminOrganisationRoutes.contentManagement} component={ContentManagement} />

      <Route exact component={Guidelines} path={adminOrganisationRoutes.hospitalGuidelines} />
      <Route
        exact
        component={GuidelinesSection}
        path={adminOrganisationRoutes.hospitalGuidelinesSubsection}
      />
      <Route
        exact
        path={adminOrganisationRoutes.expiredGuidelines}
        render={(routeProps: RouteProps) => (
          <ExpiredFilesPage
            mainSection={OrganisationLevelSection.hospitalGuidelines}
            {...routeProps}
          />
        )}
      />

      <Route exact component={AdminContactsPage} path={adminOrganisationRoutes.hospitalContacts} />
      <Route
        exact
        component={AdminContactsSection}
        path={adminOrganisationRoutes.hospitalContactsSubsection}
      />

      <Route exact component={DrugMonitoring} path={adminOrganisationRoutes.drugMonitoring} />

      {!flags[LDFlagNames.DISABLE_MEDUSA_ACTIVATION] && (
        <Route
          exact
          component={ActivateMedusaPage}
          path={adminOrganisationRoutes.medusaMedications}
        />
      )}

      <Route
        exact
        component={LearningManagement}
        path={adminOrganisationRoutes.learningManagement}
      />
      <Route
        exact
        component={LearningManagementSection}
        path={adminOrganisationRoutes.learningManagementSubsection}
      />

      <Route
        exact
        component={CommunicationsPortal}
        path={adminOrganisationRoutes.communicationPortal}
      />

      <Route
        exact
        component={OrganisationWellbeing}
        path={adminOrganisationRoutes.organisationWellbeing}
      />

      <Route
        exact
        component={OrganisationWellbeingSection}
        path={adminOrganisationRoutes.organisationWellbeingSection}
      />

      <Route
        exact
        component={GenericGuidelines}
        path={adminOrganisationRoutes.genericOrganisationGuidelines}
      />

      <Route
        exact
        component={GenericGuidelinesSection}
        path={adminOrganisationRoutes.genericOrganisationGuidelinesSection}
      />

      <Route
        exact
        path={adminOrganisationRoutes.genericOrganisationGuidelinesExpired}
        render={(routeProps: RouteProps) => (
          <ExpiredFilesPage
            mainSection={OrganisationLevelSection.genericOrganisationGuidelines}
            {...routeProps}
          />
        )}
      />

      {!hideManageUsers && (
        <Route
          exact
          path={`/${BaseAppRoute.organisation}/:organisationName/manageUsers`}
          component={ManageUsers}
        />
      )}
    </>
  );
};
